import { useState } from 'react';
import {
  Plus,
  Search,
  Headphones,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowLeft,
  X,
} from 'lucide-react';
import {
  useCases,
  useCreateCase,
  useUpdateCase,
  useCloseCase,
  useEscalateCase,
  useCase,
  type Case,
  type CaseStatus,
  type CaseFilters,
} from '../hooks/useCases';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface CasesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<CaseStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  escalated: 'Escalated',
  resolved: 'Resolved',
  closed: 'Closed',
};

const statusColors: Record<CaseStatus, string> = {
  open: 'blue',
  in_progress: 'purple',
  waiting: 'yellow',
  escalated: 'red',
  resolved: 'green',
  closed: 'gray',
};

const priorityColors: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

const categoryOptions = [
  'Billing',
  'Claims',
  'Coverage Question',
  'Endorsement',
  'General Inquiry',
  'Policy Change',
  'Renewal',
  'Other',
];

const statusTabs: { label: string; value: CaseStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Escalated', value: 'escalated' },
  { label: 'Resolved', value: 'resolved' },
];

type ViewMode = 'list' | 'form' | 'detail';

function getSlaTimeRemaining(slaDeadline?: string): string | null {
  if (!slaDeadline) return null;
  const now = Date.now();
  const deadline = new Date(slaDeadline).getTime();
  const diff = deadline - now;
  if (diff <= 0) return 'Overdue';
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  return `${hours}h ${minutes}m`;
}

// --- Case Form ---

interface CaseFormProps {
  onSave: (data: Partial<Case>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function CaseForm({ onSave, onCancel, isSaving }: CaseFormProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General Inquiry');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [policyNumber, setPolicyNumber] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      subject,
      description,
      category,
      priority,
      policyNumber: policyNumber || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Case Details</h3>
        <div>
          <label className="label">Subject</label>
          <input
            type="text"
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of the issue"
            required
          />
        </div>
        <div>
          <label className="label">Description</label>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed description..."
            rows={4}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Category</label>
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Priority</label>
            <select
              className="input"
              value={priority}
              onChange={(e) => setPriority(e.target.value as typeof priority)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Policy Number (optional)</label>
          <input
            type="text"
            className="input"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            placeholder="e.g. POL-2024-001234"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSaving || !subject.trim()}>
          {isSaving ? 'Creating...' : 'Create Case'}
        </button>
      </div>
    </form>
  );
}

// --- Case Detail Panel ---

interface CaseDetailPanelProps {
  caseId: string;
  onBack: () => void;
  onClose: (id: string, resolution: string) => void;
  onEscalate: (id: string, reason: string) => void;
}

function CaseDetailPanel({ caseId, onBack, onClose, onEscalate }: CaseDetailPanelProps) {
  const { data: caseData, isLoading } = useCase(caseId);
  const [resolution, setResolution] = useState('');
  const [escalateReason, setEscalateReason] = useState('');
  const [showClose, setShowClose] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-6 w-48 animate-pulse rounded bg-surface-200" />
        <div className="h-4 w-full animate-pulse rounded bg-surface-100" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-surface-100" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="p-6 text-sm text-slate-500">Case not found.</div>
    );
  }

  const sla = getSlaTimeRemaining(caseData.slaDeadline);
  const isOpen = caseData.status !== 'resolved' && caseData.status !== 'closed';

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to cases
      </button>

      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{caseData.subject}</h2>
            <div className="mt-1 flex items-center gap-2">
              <Badge color={statusColors[caseData.status]}>{statusLabels[caseData.status]}</Badge>
              <Badge color={priorityColors[caseData.priority]}>{caseData.priority}</Badge>
              {sla && (
                <span
                  className={cn(
                    'flex items-center gap-1 text-xs',
                    sla === 'Overdue' ? 'text-red-500 font-medium' : 'text-slate-400',
                  )}
                >
                  <Clock className="h-3 w-3" />
                  SLA: {sla}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="card p-4 space-y-3">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{caseData.description}</p>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
            <div>Category: <span className="text-slate-700">{caseData.category}</span></div>
            {caseData.assigneeName && (
              <div>Assignee: <span className="text-slate-700">{caseData.assigneeName}</span></div>
            )}
            {caseData.contactName && (
              <div>Contact: <span className="text-slate-700">{caseData.contactName}</span></div>
            )}
            {caseData.policyNumber && (
              <div>Policy: <span className="text-slate-700">{caseData.policyNumber}</span></div>
            )}
            <div>Created: <span className="text-slate-700">{new Date(caseData.createdAt).toLocaleString()}</span></div>
          </div>
        </div>

        {caseData.resolution && (
          <div className="card p-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1">Resolution</h4>
            <p className="text-sm text-slate-700">{caseData.resolution}</p>
          </div>
        )}

        {isOpen && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowClose(!showClose)}
              className="btn-primary btn-sm"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Close Case
            </button>
            {caseData.status !== 'escalated' && (
              <button
                onClick={() => setShowEscalate(!showEscalate)}
                className="btn-secondary btn-sm text-red-600 border-red-200 hover:bg-red-50"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Escalate
              </button>
            )}
          </div>
        )}

        {showClose && (
          <div className="card p-4 space-y-3">
            <h4 className="text-sm font-semibold text-slate-900">Close Case</h4>
            <textarea
              className="input"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Describe the resolution..."
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  onClose(caseId, resolution);
                  setShowClose(false);
                }}
                className="btn-primary btn-sm"
                disabled={!resolution.trim()}
              >
                Confirm Close
              </button>
              <button onClick={() => setShowClose(false)} className="btn-secondary btn-sm">
                Cancel
              </button>
            </div>
          </div>
        )}

        {showEscalate && (
          <div className="card p-4 space-y-3">
            <h4 className="text-sm font-semibold text-slate-900">Escalate Case</h4>
            <textarea
              className="input"
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              placeholder="Reason for escalation..."
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  onEscalate(caseId, escalateReason);
                  setShowEscalate(false);
                }}
                className="btn-secondary btn-sm text-red-600 border-red-200 hover:bg-red-50"
                disabled={!escalateReason.trim()}
              >
                Confirm Escalate
              </button>
              <button onClick={() => setShowEscalate(false)} className="btn-secondary btn-sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main View ---

export function CasesView({ onNavigate }: CasesViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | ''>('');
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  const filters: CaseFilters = {
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(searchText ? { search: searchText } : {}),
    limit: 100,
  };

  const { data, isLoading, error } = useCases(filters);
  const createCase = useCreateCase();
  const closeCase = useCloseCase();
  const escalateCase = useEscalateCase();

  function handleCreate() {
    setMode('form');
  }

  function handleSave(formData: Partial<Case>) {
    createCase.mutate(formData, { onSuccess: () => setMode('list') });
  }

  function handleViewDetail(caseItem: Case) {
    setSelectedCaseId(caseItem.id);
    setMode('detail');
  }

  function handleClose(id: string, resolution: string) {
    closeCase.mutate({ id, resolution }, { onSuccess: () => setMode('list') });
  }

  function handleEscalate(id: string, reason: string) {
    escalateCase.mutate({ id, reason }, { onSuccess: () => setMode('list') });
  }

  const cases = data?.cases ?? [];

  // --- Detail Mode ---
  if (mode === 'detail' && selectedCaseId) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Cases</h1>
            <p className="mt-0.5 text-sm text-slate-500">Case detail</p>
          </div>
        </div>
        <CaseDetailPanel
          caseId={selectedCaseId}
          onBack={() => setMode('list')}
          onClose={handleClose}
          onEscalate={handleEscalate}
        />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">New Case</h1>
            <p className="mt-0.5 text-sm text-slate-500">Create a new service case</p>
          </div>
        </div>
        <CaseForm
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createCase.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Cases</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} case${data.total !== 1 ? 's' : ''}` : 'Customer service cases'}
          </p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          New Case
        </button>
      </div>

      {/* Status Tabs + Search */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  statusFilter === tab.value
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setStatusFilter(tab.value as CaseStatus | '')}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search cases..."
              className="input pl-9"
            />
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load cases. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : cases.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            {cases.map((caseItem) => {
              const sla = getSlaTimeRemaining(caseItem.slaDeadline);
              return (
                <div
                  key={caseItem.id}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50 transition-colors cursor-pointer"
                  onClick={() => handleViewDetail(caseItem)}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg',
                      caseItem.status === 'escalated'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-brand-100 text-brand-600',
                    )}
                  >
                    {caseItem.status === 'escalated' ? (
                      <AlertTriangle className="h-5 w-5" />
                    ) : (
                      <Headphones className="h-5 w-5" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {caseItem.subject}
                      </span>
                      <Badge color={statusColors[caseItem.status]} size="sm">
                        {statusLabels[caseItem.status]}
                      </Badge>
                      <Badge color={priorityColors[caseItem.priority]} size="sm">
                        {caseItem.priority}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                      {caseItem.contactName && <span>{caseItem.contactName}</span>}
                      <span>{caseItem.category}</span>
                      <span>{new Date(caseItem.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {sla && (
                    <span
                      className={cn(
                        'shrink-0 flex items-center gap-1 text-xs',
                        sla === 'Overdue' ? 'text-red-500 font-medium' : 'text-slate-400',
                      )}
                    >
                      <Clock className="h-3 w-3" />
                      {sla}
                    </span>
                  )}

                  {caseItem.assigneeName && (
                    <span className="shrink-0 text-xs text-slate-500">
                      {caseItem.assigneeName}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Headphones}
            title="No cases"
            description="No service cases match your filters. Create a new case to get started."
            actionLabel="Create Case"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
